import { cn } from "@/lib/utils/index";
import { forwardRef, useEffect, useId, useState } from "react";

interface FetchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  error?: boolean;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  hideStepper?: boolean;
  type?: "text" | "number" | "password" | "email" | "tel" | "url" | "currency" | "date" | "datetime-local" | "time";
}

/**
 * Formats a number as a string with comma separators for thousands.
 */
const formatCurrencyWithCommas = (value: number | undefined | null): string => {
  if (value === undefined || value === null || isNaN(value)) {
    return "";
  }
  const parts = value.toString().split(".");
  const integerPart = parts[0];
  const decimalPart = parts[1];
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

// Hoisted — reused on every first sync of a saved currency value.
const PADDED_CURRENCY_FMT = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Pads decimals to 2 digits so a saved value like 12.5 renders canonically as "12.50".
 * Used only on the first sync from the value prop so reopening a saved currency
 * doesn't appear to drop trailing cents.
 */
const formatCurrencyWithCommasPadded = (value: number | undefined | null): string => {
  if (value === undefined || value === null || !Number.isFinite(value)) return "";
  return PADDED_CURRENCY_FMT.format(value);
};

/**
 * Parses a string value that may contain commas, removing them and converting to a number.
 */
const parseCurrencyValue = (value: string): string => {
  return value.replace(/,/g, "");
};

/**
 * Nowadays themed input component.
 */
export const FetchInput = forwardRef<HTMLInputElement, FetchInputProps>(
  (
    {
      className,
      error,
      helperText,
      disabled,
      leftIcon,
      rightIcon,
      type,
      onKeyDown,
      onChange,
      hideStepper,
      value,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      ...props
    },
    ref
  ) => {
    const isCurrency = type === "currency";
    const [displayValue, setDisplayValue] = useState<string>("");
    // Announce + associate the helper/error text so screen readers link it to the input.
    const helperId = useId();
    const describedBy = [ariaDescribedBy, helperText ? helperId : undefined].filter(Boolean).join(" ") || undefined;

    useEffect(() => {
      if (isCurrency && value !== undefined && value !== null) {
        let numValue: number | undefined | null;
        if (typeof value === "string") {
          numValue = value === "" ? undefined : parseFloat(value);
        } else if (typeof value === "number") {
          numValue = value;
        } else {
          numValue = undefined;
        }
        setDisplayValue((prev) => {
          // First sync from the value prop (display still empty) — pad decimals to 2
          // digits so a saved 12.5 reopens as "12.50" instead of "12.5".
          if (prev === "") return formatCurrencyWithCommasPadded(numValue);
          // If the parsed numeric value matches what's already displayed (e.g. user
          // just typed "12.50" and onChange echoed back 12.5), preserve the user's
          // typed format — including any trailing zeros they entered.
          const prevNum = Number(parseCurrencyValue(prev));
          if (Number.isFinite(prevNum) && prevNum === numValue) return prev;
          return formatCurrencyWithCommas(numValue);
        });
      } else if (!isCurrency) {
        setDisplayValue(value?.toString() || "");
      }
    }, [value, isCurrency]);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (type === "number" || type === "currency") {
        const allowedKeys = [
          "Backspace",
          "Delete",
          "Tab",
          "Escape",
          "Enter",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
          ".",
        ];
        const isControlKey = e.ctrlKey || e.metaKey;
        const isAllowedKey = allowedKeys.includes(e.key) || isControlKey;

        if (!isAllowedKey && !/[0-9]/.test(e.key)) {
          e.preventDefault();
        }
      }
      onKeyDown?.(e);
      if (e.key === "Escape" && !e.defaultPrevented) {
        e.currentTarget.blur();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (type === "number") {
        const value = e.target.value;
        const numericValue = value.replace(/[^0-9.]/g, "");
        const parts = numericValue.split(".");
        const decimalValue = parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : numericValue;
        if (value !== decimalValue) {
          e.target.value = decimalValue;
        }
        onChange?.(e);
      } else if (type === "currency") {
        const rawValue = parseCurrencyValue(e.target.value);
        const numericValue = rawValue.replace(/[^0-9.]/g, "");
        const parts = numericValue.split(".");
        let decimalValue = parts.length > 2 ? parts[0] + "." + parts.slice(1).join("") : numericValue;

        const endsWithDecimal = numericValue.endsWith(".");
        if (endsWithDecimal && !decimalValue.endsWith(".")) {
          decimalValue = decimalValue + ".";
        }

        let formatted: string;
        if (decimalValue === "" || decimalValue === ".") {
          formatted = "";
        } else {
          const decimalParts = decimalValue.split(".");
          const integerPart = decimalParts[0];
          const decimalPart = decimalParts[1];

          const integerNum = integerPart === "" ? 0 : parseFloat(integerPart);
          const formattedInteger = formatCurrencyWithCommas(integerNum);

          if (decimalPart !== undefined) {
            formatted = `${formattedInteger}.${decimalPart}`;
          } else if (endsWithDecimal) {
            formatted = `${formattedInteger}.`;
          } else {
            formatted = formattedInteger;
          }
        }

        setDisplayValue(formatted);

        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: decimalValue,
          },
        } as React.ChangeEvent<HTMLInputElement>;

        onChange?.(syntheticEvent);
      } else {
        onChange?.(e);
      }
    };

    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      if (type === "number" || type === "currency") {
        e.currentTarget.blur();
      }
    };

    return (
      <div className="w-full">
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">{leftIcon}</div>
          )}
          {rightIcon && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">{rightIcon}</div>
          )}
          <input
            ref={ref}
            type={isCurrency ? "text" : type}
            value={isCurrency ? displayValue : value}
            className={cn(
              "touch-manipulation text-gray-800 font-medium w-full rounded-lg border border-gray-200 bg-white py-2 text-base sm:text-sm",
              "focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 focus:outline-none transition-all duration-200",
              "focus:shadow-[0_0_0_3px_rgba(165,180,252,0.2)]",
              leftIcon && rightIcon ? "pl-10 pr-10" : leftIcon ? "pl-10 pr-3" : rightIcon ? "pl-3 pr-10" : "px-3",
              error ? "border-red-400 focus:border-red-500 focus:ring-red-200 focus:shadow-none" : "",
              disabled ? "cursor-not-allowed opacity-50" : "",
              hideStepper && (type === "number" || type === "currency")
                ? "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                : "",
              className
            )}
            style={
              hideStepper && (type === "number" || type === "currency") ? { MozAppearance: "textfield" } : undefined
            }
            disabled={disabled}
            aria-invalid={ariaInvalid ?? (error ? true : undefined)}
            aria-describedby={describedBy}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            onWheel={handleWheel}
            {...props}
          />
        </div>
        {helperText && (
          <p
            id={helperId}
            role={error ? "alert" : undefined}
            // This helper used to carry no weight class at all, which left a red "Required"
            // reading lighter than the label above it. An error outweighs a neutral hint, so the
            // two states take different weights: `font-semibold` for the error, `font-medium` for
            // the hint - the latter being the repo's "font-medium, not font-regular, for text-xs
            // and up" floor.
            className={cn("mt-1 text-xs", error ? "font-semibold text-red-600" : "font-medium text-gray-600")}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FetchInput.displayName = "FetchInput";
